import React from "react";

import { Box, Grid, Typography, Tooltip, TextField } from "@material-ui/core";
import {
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from "@material-ui/lab";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import Filter4Icon from "@material-ui/icons/Filter4";
import Filter6Icon from "@material-ui/icons/Filter6";
import Filter8Icon from "@material-ui/icons/Filter8";
import ListAltIcon from "@material-ui/icons/ListAlt";

import { SearchField } from "../../../../components/common/SearchField";
import { MuiAccountDropDown } from "../../../../components/common/MuiAccountDropDown";
import { MuiProductDropDown } from "../../../../components/common/MuiProductDropDown";
import { AntSwitch } from "../../../../components/common/AntSwitch";

import { getAisleFullName } from "../../../../utils/locationUtils";
import { useTrackContext } from "../TrackContext";

/**
 * TrackFilters component for filtering and view mode selection
 * Consumes from TrackContext to reduce prop drilling
 */
export const TrackFilters = () => {
  const {
    setSearchTerm,
    accountFilter,
    setAccountFilter,
    productFilter,
    setProductFilter,
    trackFilter,
    setTrackFilter,
    allTracks: tracks = [],
    compactMode,
    setCompactMode,
    gridSize,
    setGridSize,
    viewMode,
    setViewMode,
    isCustomer,
    customerAccountId,
    onAccountsLoaded,
  } = useTrackContext();

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    setSearchTerm(searchTerm);
  };

  const handleAccountChange = (selectedAccount) => {
    setAccountFilter(selectedAccount || null);
    if (productFilter) {
      setProductFilter(null);
    }
  };

  const handleProductChange = (selectedProduct) => {
    setProductFilter(selectedProduct || null);
  };

  const handleTrackChange = (event, newValue) => {
    setTrackFilter(
      newValue.map((t) => t.locations?.items?.[0]?.id).filter(Boolean),
    );
  };

  // Prepare track options for Autocomplete
  const trackOptions = React.useMemo(() => {
    return tracks.filter((t) => t.locations?.items?.[0]?.id);
  }, [tracks]);

  // Current selected track objects for Autocomplete
  const selectedTrackObjects = React.useMemo(() => {
    return trackOptions.filter((t) =>
      trackFilter.includes(t.locations?.items?.[0]?.id),
    );
  }, [trackOptions, trackFilter]);

  return (
    <Box display="flex" flexDirection="column" width="100%" gap={1}>
      {/* Row 1: Primary Data Filters */}
      <Grid
        container
        spacing={2}
        alignItems="center"
        id="track-management-filters"
        data-productly="track-management-filters"
      >
        {!isCustomer && (
          <>
            <Grid item xs={12} sm={3}>
              <MuiAccountDropDown
                label="Account"
                value={accountFilter || null}
                onChange={handleAccountChange}
                onAccountsLoaded={onAccountsLoaded}
                fullWidth
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MuiProductDropDown
                label="Product"
                accountId={accountFilter?.id}
                value={productFilter || null}
                onChange={handleProductChange}
                disabled={!accountFilter?.id}
                fullWidth
                clearOnEscape
              />
            </Grid>
          </>
        )}
        {isCustomer && customerAccountId && (
          <Grid item xs={12} sm={6}>
            <MuiProductDropDown
              label="Product"
              accountId={customerAccountId}
              value={productFilter || null}
              onChange={handleProductChange}
              fullWidth
              clearOnEscape
            />
          </Grid>
        )}
        <Grid item xs={12} sm={3} data-productly="track-management-track-filter">
          <Autocomplete
            multiple
            id="track-filter"
            options={trackOptions}
            getOptionLabel={(option) =>
              getAisleFullName(option) || "Unknown Track"
            }
            value={selectedTrackObjects}
            onChange={handleTrackChange}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Filter by Track(s)"
                placeholder="Tracks..."
                size="small"
              />
            )}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3} data-productly="track-management-search">
          <SearchField
            label="Search railcar"
            margin="none"
            size="small"
            variant="outlined"
            alwaysOn
            fullWidth
            handleSearch={handleSearch}
            searchOnChange={true}
            style={{ marginBottom: 0 }}
          />
        </Grid>
      </Grid>

      {/* Row 2: Layout & View Controls */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
        mt={0.5}
        gap={3}
        id="track-management-layout-controls"
        data-productly="track-management-layout-controls"
      >
        {/* Compact Mode Toggle */}
        <Box
          display="flex"
          alignItems="center"
          id="track-management-compact-mode"
          data-productly="track-management-compact-mode"
        >
          <AntSwitch
            checked={compactMode}
            onChange={(e) => setCompactMode(e.target.checked)}
            color="primary"
          />
          <Typography
            variant="caption"
            style={{ marginLeft: 8, whiteSpace: "nowrap", fontWeight: 500 }}
          >
            Compact Mode
          </Typography>
        </Box>

        {/* View Selection */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          ml={4}
          id="track-management-view-toggle"
          data-productly="track-management-view-toggle"
        >
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ whiteSpace: "nowrap" }}
          >
            View:
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, next) => {
              if (next !== null) {
                setViewMode(next);
              }
            }}
            size="small"
            aria-label="view mode selection"
            data-productly="track-management-view-toggle-group"
          >
            <Tooltip title="Grid View">
              <ToggleButton
                value="grid"
                aria-label="grid view"
                selected={viewMode === "grid"}
              >
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="List View">
              <ToggleButton
                value="list"
                aria-label="list view"
                selected={viewMode === "list"}
              >
                <ListAltIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>

        {/* Grid Density (only in Grid View) */}
        {!isCustomer && viewMode === "grid" && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            id="track-management-columns"
            data-productly="track-management-columns"
          >
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ whiteSpace: "nowrap" }}
            >
              Columns:
            </Typography>
            <ToggleButtonGroup
              value={gridSize}
              exclusive
              onChange={(e, next) => {
                if (next !== null) {
                  setGridSize(next);
                }
              }}
              size="small"
              aria-label="track column selection"
              data-productly="track-management-column-selection"
            >
              <Tooltip title="4 Columns (Standard)">
                <ToggleButton
                  value={4}
                  aria-label="4 columns"
                  selected={gridSize === 4}
                >
                  <Filter4Icon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="6 Columns (Dense)">
                <ToggleButton
                  value={6}
                  aria-label="6 columns"
                  selected={gridSize === 6}
                >
                  <Filter6Icon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="8 Columns (Ultra)">
                <ToggleButton
                  value={8}
                  aria-label="8 columns"
                  selected={gridSize === 8}
                >
                  <Filter8Icon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
    </Box>
  );
};
